import React, { useMemo, useId, forwardRef } from 'react';
import { ValidationError } from '@react-types/shared';
import {
  ValidationResult,
  Checkbox as AriaCheckbox,
  CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';
import { cva } from 'class-variance-authority';
import { normalizeBooleanProp } from '../../utils/helpers/normalizeBooleanProp';

import FieldDescription from '../FieldDescription';
import FieldError from '../FieldError';
import styles from './Checkbox.module.css';

// Extracted constant to avoid recreation on every render
const DEFAULT_VALIDATION_RESULT: ValidationResult = {
  isInvalid: true,
  validationErrors: ['Invalid'],
  validationDetails: { valid: false } as unknown as ValidityState,
};

export interface CheckboxProps extends AriaCheckboxProps {
  /** The label of the checkbox, can be a string or a React node. */
  label?: string | React.ReactNode;
  /** Indeterminism is presentational only. The indeterminate visual representation remains regardless of user interaction. */
  isIndeterminate?: boolean;
  /** Whether the element should be selected (uncontrolled). */
  defaultSelected?: boolean;
  /** Whether the element should be selected (controlled). */
  isSelected?: boolean;
  /** The value of the input element, used when submitting an HTML form. See MDN. */
  value?: string;
  /** Whether the input is disabled. */
  isDisabled?: boolean;
  /** Whether the input can be selected but not changed by the user. */
  isReadOnly?: boolean;
  /** Whether user input is required on the input before form submission. */
  isRequired?: boolean;
  /** Whether the input value is invalid. */
  isInvalid?: boolean;
  /** A function that returns an error message if a given value is invalid. Validation errors are displayed to the user when the form is submitted if validationBehavior="native". For realtime validation, use the isInvalid prop instead. */
  validate?: (value: unknown) => ValidationError | true | null | undefined;
  /** Whether the element should receive focus on render. */
  autoFocus?: boolean;
  /** The name of the input element, used when submitting an HTML form. See MDN. */
  name?: string;
  /**
   * Whether to use native HTML form validation to prevent form submission
   * when the value is missing or invalid, or mark the field as required
   * or invalid via ARIA.
   * @default 'aria'
   */
  validationBehavior?: 'native' | 'aria';
  /** Description text for the checkbox */
  description?: React.ReactNode;
  /** Alternate way to provide label content */
  children?: React.ReactNode;
  /** Focus style variant */
  focusStyle?: 'default' | 'white';
  /**
   * Error message to display when the checkbox is invalid
   * Can be a static string or a function that returns an error message
   * @default undefined
   */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /**
   * Prefix text for screen readers only in error messages
   * @default 'Error:'
   */
  errorPrefix?: string;
  /**
   * Custom test ID for testing purposes
   * @default undefined
   */
  testId?: string;
}

const checkbox = cva(styles.checkbox, {
  variants: {
    focusStyle: {
      default: styles.defaultFocus,
      white: styles.whiteFocus,
    },
  },
  defaultVariants: {
    focusStyle: 'default',
  },
});

export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      label,
      isIndeterminate,
      defaultSelected,
      isSelected,
      value,
      isDisabled,
      isReadOnly,
      isRequired,
      isInvalid,
      validate,
      autoFocus,
      name,
      validationBehavior,
      description,
      children,
      errorMessage,
      errorPrefix = 'Error:',
      testId,
      focusStyle = 'default',
      ...props
    },
    forwardedRef,
  ) => {
    // Generate unique IDs for accessibility
    const errorId = useId();
    const descriptionId = useId();

    // Resolve error message with memoization
    const resolvedErrorMessage = useMemo(() => {
      if (!isInvalid) return null;

      if (typeof errorMessage === 'function') {
        return errorMessage(DEFAULT_VALIDATION_RESULT);
      }

      return errorMessage;
    }, [errorMessage, isInvalid]);

    // Determine aria-describedby based on description, error, or both
    const describedById = [
      description ? descriptionId : null,
      isInvalid && resolvedErrorMessage ? errorId : null,
    ]
      .filter(Boolean)
      .join(' ');

    // Determine the final label content
    const labelContent = label || children;

    // Find where isDisabled is used and ensure it is passed through normalizeBooleanProp before use.
    // Example: const resolvedIsDisabled = normalizeBooleanProp(isDisabled);
    // Use resolvedIsDisabled wherever isDisabled is passed to the underlying component.
    const resolvedIsDisabled = normalizeBooleanProp(isDisabled);

    return (
      <div className={styles.checkboxContainer} data-testid={testId}>
        {isInvalid && resolvedErrorMessage && (
          <FieldError
            id={errorId}
            errorPrefix={errorPrefix}
            isInvalid={isInvalid}
          >
            {resolvedErrorMessage}
          </FieldError>
        )}
        <div className={styles.checkboxLabelWrapper}>
          <AriaCheckbox
            ref={forwardedRef}
            className={checkbox({ focusStyle })}
            isIndeterminate={isIndeterminate}
            defaultSelected={defaultSelected}
            isSelected={isSelected}
            value={value}
            isDisabled={resolvedIsDisabled}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            isInvalid={isInvalid}
            validate={validate}
            autoFocus={autoFocus}
            name={name}
            validationBehavior={validationBehavior}
            aria-describedby={describedById || undefined}
            {...props}
          >
            {({ isIndeterminate }: { isIndeterminate: boolean }) => (
              <>
                <div
                  className={`${styles.checkboxSvgWrapper}`}
                  data-disabled={isDisabled}
                >
                  <svg
                    className={styles.svg}
                    viewBox="0 0 18 18"
                    aria-hidden="true"
                  >
                    {isIndeterminate ? (
                      <rect x={1} y={7.5} width={15} height={3} />
                    ) : (
                      <polyline points="1 9 7 14 15 4" />
                    )}
                  </svg>
                </div>
                {labelContent ? (
                  <span className={styles.checkboxLabel}>{labelContent}</span>
                ) : null}
              </>
            )}
          </AriaCheckbox>
          {description && (
            <FieldDescription
              id={descriptionId}
              className={styles.checkboxDescription}
            >
              {description}
            </FieldDescription>
          )}
        </div>
      </div>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export default Checkbox;
